const PictViewClass = require('pict-view');

class LIMSDocumentRecordView extends PictViewClass
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {import('../LIMS-Application-Web').LIMSPict} */
		this.pict;

		this.AppData.DocumentDetails = {};
		this.ActiveDocumentLogs = [];
		this.LIMSDocumentRecordBackTo = null;
		this.manifest = require('./defaultDocumentViewManifest.json');
		this.pict.MetaTemplate.addPattern('{~ShowOnLocked:', '~}', (pHash, pData, fCallback) =>
		{
			if (this.AppData.DocumentDetails?.CanEdit && !this.AppData.DocumentDetails?.Locked)
			{
				return 'is-hidden';
			}
			return '';
		});
		this.pict.MetaTemplate.addPattern('{~HideOnLocked:', '~}', (pHash, pData, fCallback) =>
		{
			if (this.AppData.DocumentDetails?.CanEdit && !this.AppData.DocumentDetails?.Locked)
			{
				return '';
			}
			return 'is-hidden';
		});
	}

	async onBeforeRenderAsync(pCallback, pRenderable)
	{
		this.AppData.DocumentDetails = {};
		const session = await this.pict.providers.SessionProvider.getSession();
		this.pict.PictApplication.changeTheme('Read');
		if (!this.initialized || (session.CustomerID !== this.customer))
		{
			this.customer = session.CustomerID;
			// Check for a manifest from the DB before loading the default.
			const remoteManifest = await this.pict.ManifestPackagingService.loadRecordManifestSchema('Document', 'View', this.pict.PictApplication.options.pict_configuration.Product);
			this.manifest = remoteManifest || this.manifest;
			this.pict.views.PictFormMetacontroller.clearManifestDescription();
			this.pict.views.PictFormMetacontroller.bootstrapPictFormViewsFromManifest(this.manifest);
			this.pict.views['PictSectionForm-DocumentView'].viewMarshalDestination = 'AppData';
			this.pict.views['PictSectionForm-DocumentView'].options.DefaultDestinationAddress = '#LIMSDocumentRecordView-View-Container';
			// @ts-ignore
			for (const pickList of this.manifest.PickLists || [])
			{
				this.pict.providers.DynamicMetaLists.rebuildListByHash(pickList.Hash);
			}
			this.pict.views['PictSectionForm-DocumentView'].rebuildCustomTemplate();
			this.initialized = true;
		}
		super.onBeforeRenderAsync(async () =>
		{
			try
			{
				this.pict.views['PictSectionForm-DocumentView'].rebuildCustomTemplate();
				this.log.info('Document Details view rendered with Active Record', this.AppData.DocumentDetails);

				// get the IDDocument from the router parameters
				const tmpGUIDDocument = this.pict.providers.LIMSRouter.currentView.split('/')[1];
				if (tmpGUIDDocument)
				{
					this.log.info('Document Details view opened with GUIDDocument', tmpGUIDDocument);
					// get the Document record by ID
					const tmpRecord = await this.pict.providers.DocumentProvider.getDocumentByGUID(tmpGUIDDocument);
					if (tmpRecord)
					{
						this.setDocumentDetails(tmpRecord);
						this.log.info('Document Details view opened with Document record', this.AppData.DocumentDetails);
					}
					else
					{
						this.log.error('Document record not found for ID', tmpGUIDDocument);
					}
				}
			}
			catch (error)
			{
				this.log.error('Error in Document Details onBeforeRenderAsync:', error);
			}
			return pCallback();
		}, pRenderable);
	}

	/**
	 * Bind a Document record to the details view, adding the state label the screen should show.
	 *
	 * An approval workflow carries two names per state: `Status` is the hash it keys transitions off
	 * (`InTest`, `Pending_DLE`, `Submitted`) and `StateName` is the name authored for people ("In Test",
	 * "Waiting for District Lab Engineer Approval", "Submitted for Review"). This view labels the field
	 * "Status" to the user, so the authored name is the one that belongs there - and it is what the form's
	 * own toolbar shows, so reading the hash here made the two disagree on the same document.
	 *
	 * The fallback matters: a Draft is created by the API service without consulting the approval
	 * workflow, so it carries a Status and no StateName at all. Mirrors
	 * `web/headlight_panels/source/behaviors/DocumentStateDisplay.js`, which does the same for the panels.
	 *
	 * @param {Record<string, any>} pRecord - the Document record to bind.
	 */
	setDocumentDetails(pRecord)
	{
		const tmpRecord = pRecord || {};
		const tmpText = (pValue) => (pValue === undefined || pValue === null) ? '' : String(pValue).trim();
		tmpRecord.StatusDisplay = tmpText(tmpRecord.StateName) || tmpText(tmpRecord.Status);
		this.AppData.DocumentDetails = tmpRecord;
	}

	/**
	 * Open the Document detail view for the selected Document to view the full details
	 * @param {number|string} pIDDocument - The ID of the Document to view
	 * @param {object} pDocumentRecord - The Document record to view
	 * @param {string} pOpenTab - The tab to open when the Document detail view is shown
	 * @return {Promise<void>}
	 * @async
	 */
	async showDocumentDetailView(pIDDocument, pDocumentRecord, pOpenTab = 'Details')
	{
		// if a Document record is passed in, use it, otherwise get the Document record by ID
		if (pDocumentRecord)
		{
			this.setDocumentDetails(Array.isArray(pDocumentRecord) ? pDocumentRecord[0] : pDocumentRecord);
		}
		else
		{
			const tmpRecord = await this.pict.providers.DocumentProvider.getDocumentByID(pIDDocument);
			this.setDocumentDetails(tmpRecord);
		}
		// render the Document detail view
		await new Promise((resolve, reject)=>
		{
			this.renderAsync(() =>
			{
				this.log.info('Document Details View Rendered with Active Record', this.AppData.DocumentDetails);
				resolve();
			});
		});
	}

	/**
	 * Lifecycle hook that triggers after the view is rendered.
	 *
	 * @param {import('pict-view').ErrorCallback} fCallback
	 * @param {import('pict-view').Renderable} pRenderable
	 */
	onAfterRenderAsync(fCallback, pRenderable)
	{
		this.pict.views['PictSectionForm-DocumentView'].render();
		this.pict.views['PictSectionForm-DocumentView'].marshalToView();

		return super.onAfterRenderAsync(fCallback, pRenderable);
	}

	goToEditView()
	{
		if (!this.AppData.DocumentDetails)
		{
			this.log.error('[Document-Record-View] No active Document to edit');
			return;
		}
		this.log.info('[Document-Record-View] Going to Edit Document view for Document GUID:', this.AppData.DocumentDetails.GUIDDocument);
		this.pict.providers.LIMSRouter.router.navigate(`/Document/${this.AppData.DocumentDetails.GUIDDocument}/Edit`);
	}

	confirmDeleteDocument()
	{
		// prompt a confirmation modal to do the deletion
		this.fable.HeadlightModal.spawnModal({
			title: 'Delete Document',
			modalBodyTemplate: /*html*/`
			<div class="container column is-12">
				<p class="title is-5
				has-text-centered p-2 mb-5">
					<span class="icon">
						<i class="fa fa-fw fa-exclamation-triangle has-text-danger"></i>
					</span>
					<span> Are you sure you want to delete this Document?</span>
				</p>
				<p class="is-6 has-text-centered">This action is irreversible and will permanently delete</p>
				<p class="has-text-centered m-3"><strong>${ this.AppData.DocumentDetails.Name }</strong></p>
				<p class="is-6 has-text-centered">Please click button below to confirm deletion or close this modal.</p>
			</div>`,
			modalFooterTemplate: /*html*/`
			<div class="buttons is-right">
				<button class="button is-danger is-soft" type="button" title="Cancel Deletion" onclick="_Pict.fable.services.HeadlightModal.dismissModals()">
					<span class="icon">
						<i class="fas fa-fw fa-times" aria-hidden="true"></i>
					</span>
					<span>Cancel</span>
				</button>
				<button class="button is-danger" type="button" title="Delete Document" onclick="_Pict.views.LIMSDocumentRecordView.deleteDocument()">
					<span class="icon">
						<i class="fas fa-fw fa-trash" aria-hidden="true"></i>
					</span>
					<span>Delete Document</span>
				</button>
			</div>`,
			confirmText: 'Delete Document',
			cancelText: 'Cancel',
		});
	}

	deleteDocument()
	{
		if (!this.AppData.DocumentDetails)
		{
			this.log.error('[Document-Record-View] No active Document to delete');
			return;
		}
		this.log.info('[Document-Record-View] Deleting Document ID:', this.AppData.DocumentDetails.IDDocument);
		this.pict.HeadlightRestClient.deleteEntity('Document', this.AppData.DocumentDetails.IDDocument, () =>
		{});
		this.pict.providers.LIMSRouter.router.navigate('/PSRS/Document/List');
		this.pict.fable.services.HeadlightModal.dismissModals();
	}

	toggleTabContent(event, pTabID)
	{
		// clear the active class from all tabs
		const tmpTabs = document.querySelectorAll('.tabs ul li');
		tmpTabs.forEach((tab) =>
		{
			tab.classList.remove('is-active');
		});
		// set the active class on the selected tab
		const tmpActiveTab = event.currentTarget;
		if (tmpActiveTab)
		{
			tmpActiveTab?.parentElement.classList.add('is-active');
		}
		// hide all the tab content
		const tmpTabContent = document.querySelectorAll('.tab-content');
		tmpTabContent.forEach((content) =>
		{
			content.classList.remove('is-active');
		});
		// show the selected tab content
		const tmpActiveTabContent = document.getElementById(`${pTabID}-Content`);
		if (tmpActiveTabContent)
		{
			tmpActiveTabContent.classList.add('is-active');
		}
	}
}

module.exports = LIMSDocumentRecordView;

module.exports.default_configuration =
{
	ViewIdentifier: 'LIMSDocumentRecordView',
	DefaultRenderable: 'LIMSDocumentRecordView',
	DefaultDestinationAddress: '#LIMS-Application-Container',
	CSS: /*css*/`
		.tab-content {
			display: none;
		}
		.tab-content.is-active {
			display: block;
		}`,
	Templates:
	[
		{
			Hash: 'LIMSDocumentRecordView-Wrapper',
			Template: /*html*/`
			<form class="container form" data-document-id="{~D:Record.IDDocument~}">
				{~T:LIMSDocumentRecordView-DocumentHeader:AppData.DocumentDetails~}
				<!-- Document Panel Tabs -->
				{~T:LIMSDocumentRecordView-Panel-Tabs:AppData.DocumentDetails~}
				<!-- LIMSDocumentRecord Content > Tab Details View -->
				<div id="LIMSDocumentRecordViewPanel-Details-Content" class="tab-content is-active">
					<div id="LIMSDocumentRecordView-Details-Container">{~T:LIMSDocumentRecordView-Tab-Details:AppData.DocumentDetails~}</div>
				</div>
			</form>`,
		},
		{
			Hash: 'LIMSDocumentRecordView-Panel-Tabs',
			Template: /*html*/`
			<nav class="tabs is-right mt-2">	
				<ul>				
					<li class="is-active">
						<a id="LIMSDocumentRecordViewPanel-Details-Tab" class="is-active" onclick="_Pict.views.LIMSDocumentRecordView.toggleTabContent(event, 'LIMSDocumentRecordPanel-Details')">
							<span class="icon">
								<i class="fa fa-fw fa-info-circle"></i>
							</span>
							<span class="icon-text">
								Details
							</span>
						</a>
					</li>
					<li>
						<a id="LIMSDocumentRecordViewPanel-Open-Tab" onclick="_Pict.providers.DocumentProvider.openFormByIDDocument(event, {~D:Record.IDDocument~});">
							<span class="icon">
								<i class="fa fa-fw fa-external-link-alt"></i>
							</span>
							<span class="icon-text">
								Open
							</span>
						</a>
					</li>
					<li>
						<a id="LIMSDocumentRecordViewPanel-Print-Tab" onclick="_Pict.providers.DocumentProvider.viewDocumentFile('_blank', false, {~D:Record.IDDocument~});">
							<span class="icon">
								<i class="fa fa-fw fa-print"></i>
							</span>
							<span class="icon-text">
								Print
							</span>
						</a>
					</li>
				</ul>
			</nav>`,
		},
		{
			Hash: 'LIMSDocumentRecordView-DocumentHeader',
			Template: /*html*/`
			<div class="container columns has-background-text-soft is-mobile is-vcentered mb-0" style="min-height: 130px">
				<div class="column px-5">
					<p class="title is-4">
						{~TIfAbs:LIMSDocumentRecordView-DatumValue-Name:Record:Record.Name^>^0~}
						{~TIfAbs:LIMSDocumentRecordView-DatumValue-Name-Empty:Record:Record.Name^<=^0~}
					</p>
					<p class="subtitle is-6 has-text-text-50 mb-2">
						<span class="icon">
							<i class="fas fa-fw fa-file fa-sm"></i>
						</span>
						<span class="icon-text">Type: {~DocumentTypeName:Record.DocumentType~}</span>
					</p>
				</div>
			</div>`,
		},
		{
			Hash: 'LIMSDocumentRecordView-Tab-Details',
			Template: /*html*/`
			<!-- Placeholder for dynamic section -->
			<div id="LIMSDocumentRecordView-View-Container" class="app-page-gutters-for-mobile px-5 mt-2"></div>
			{~T:LIMSDocumentRecordView-Tab-Details-Buttons-Read:AppData.DocumentDetails~}`,
		},
		{
			Hash: 'LIMSDocumentRecordView-Tab-Details-Buttons-Read',
			Template: /*html*/`
			<div class="buttons my-2 mr-6 is-right">
				<!-- Delete Document Button -->
				<button type="button" id="LIMSDocumentRecordViewPanel-Details-Delete-Button" class="button is-link is-outlined {~HideOnLocked:AppData~}" onclick="_Pict.views.LIMSDocumentRecordView.confirmDeleteDocument()">
					<span class="icon">
						<i class="fa fa-fw fa-trash"></i>
					</span>
					<span class="icon-text">Delete Document</span>
				</button>
				<button title="This Document cannot be edited." type="button" id="LIMSDocumentRecordViewPanel-Details-Delete-Button" disabled class="is-disabled button is-link is-outlined {~ShowOnLocked:AppData~}">
					<span class="icon-text">Delete Document</span>
				</button>
				<!-- Edit Document Button -->
				<button type="button" id="LIMSDocumentRecordViewPanel-Logs-Add-Button" class="button is-link {~HideOnLocked:AppData~}" onclick="_Pict.views.LIMSDocumentRecordView.goToEditView()">
					<span class="icon">
						<i class="fa fa-fw fa-pencil"></i>
					</span>
					<span class="icon-text">Edit Document Metadata</span>
				</button>
				<button title="This Document cannot be edited." type="button" id="LIMSDocumentRecordViewPanel-Logs-Add-Button" disabled class="is-disabled button is-link {~ShowOnLocked:AppData~}">
					<span class="icon">
						<i class="fa fa-fw fa-lock"></i>
					</span>
					<span class="icon-text">Edit Document Metadata</span>
				</button>
			</div>`,
		},
		{
			Hash: 'LIMSDocumentRecordView-EntityValue-NA',
			Template: /*html*/`<span class="has-text-text-50">N/A</span>`,
		},
		{
			Hash: 'LIMSDocumentRecordView-DatumValue',
			Template: /*html*/`<span class="has-text-text-50">N/A</span>`,
		},
		{
			Hash: 'LIMSDocumentRecordView-DatumValue-NA',
			Template: /*html*/`<span class="has-text-text-50">N/A</span>`,
		},
		{
			Hash: 'LIMSDocumentRecordView-DatumValue-Name',
			Template: /*html*/`{~D:Record.Name~}`,
		},
		{
			Hash: 'LIMSDocumentRecordView-DatumValue-Name-Empty',
			Template: /*html*/`Unnamed Document #{~D:Record.IDDocument~}`,
		},
		{
			Hash: 'LIMSDocumentRecordView-DatumValue-Description',
			Template: /*html*/`{~D:Record.Description~}`,
		},
		{
			Hash: 'LIMSDocumentRecordView-DatumValue-ParentDocument',
			Template: /*html*/`{~D:Record.ParentDocument.Name~}`,
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'LIMSDocumentRecordView',
			TemplateHash: 'LIMSDocumentRecordView-Wrapper',
			DestinationAddress: '#LIMS-Application-Container',
			RenderType: 'replace',
		},
	],
};
